import React, { useEffect, useState } from 'react';
import BasePageLayout from '../../../common/components/BasePageLayout/BasePageLayout';
import BaseCard from '../../../components/cards/BaseCard/BaseCard';
import styles from '../AdminUsersPage/AdminUsersPage.module.scss';
import Pagination from '../../../common/components/Pagination/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../reducers/store';
import { useTranslate } from 'react-translate.ts';
import usePagination from '../../../common/hooks/usePagination';
import AdminCustomerSlice from '../../slices/AdminCustomerSlice';
import Table from '../../../components/table/Table/Table';
import {
    ButtonVariants,
    LoadingOverlay,
    Skeleton,
} from '@tcc-online/common-components';
import IAdminCustomer from '../../types/IAdminCustomer';
import ListDeleteButton from '../../components/list/ListDeleteButton/ListDeleteButton';
import DeleteDialog from '../../../components/dialog/DeleteDialog/DeleteDialog';
import AdminButton from '../../components/buttons/AdminButton/AdminButton';
import CreateDialog from '../../components/form/CreateDialog/CreateDialog';
import { FormInput } from '@tcc-online/input-components';
import { Formik } from 'formik';
import { ObjectSchema, StringSchema } from 'yup';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { ADMIN_COMPANY_PATH } from '../../../constants/route.constants';
import { isSuccessfulResponse } from '../../../helpers';

interface DialogState {
    active: boolean;
    company?: IAdminCustomer;
}

const NAME_FIELD = 'name';

interface Values {
    name: string;
}

const validationSchema = new ObjectSchema({
    [NAME_FIELD]: new StringSchema().nonNullable().required(),
});

export default function AdminCustomersPage() {
    const {
        data: companies,
        loading,
        pagination,
    } = useSelector(AdminCustomerSlice.state.list);
    const dispatch = useDispatch<AppDispatch>();
    const translate = useTranslate();
    const navigate = useNavigate();
    const { page, changePage } = usePagination();
    const [deleteDialogState, setDeleteDialogState] = useState<DialogState>({
        active: false,
    });
    const [createDialogState, setCreateDialogState] = useState(false);

    useEffect(() => {
        dispatch(AdminCustomerSlice.actions.getCustomers({ page }));
    }, [dispatch, page]);

    return (
        <BasePageLayout heading={translate('Zakaznici')}>
            <AdminButton
                className={styles.add}
                variant={ButtonVariants.positive}
                onClick={showCreateDialog}
            >
                {translate('Přidat společnost')}
            </AdminButton>
            <BaseCard className={styles.page}>
                <Table className={styles.table}>
                    <Table.Row className={styles.row}>
                        <div>{translate('Nazev')}</div>
                    </Table.Row>
                    <div>
                        <LoadingOverlay isLoading={loading} isTransparent />
                        {loading && companies?.length === 0
                            ? Array.from(new Array(10), (_, index: number) => (
                                  <Skeleton key={index} height={73} />
                              ))
                            : companies.map((company) => (
                                  <Item
                                      key={company.id}
                                      company={company}
                                      isLoading={loading}
                                      onDelete={showDeleteDialog}
                                  />
                              ))}
                    </div>
                </Table>
                <DeleteDialog
                    isVisible={deleteDialogState.active}
                    onClose={closeDialog}
                    onCancel={closeDialog}
                    onConfirm={deleteCompany(deleteDialogState.company)}
                >
                    {deleteDialogState.company &&
                        translate('Opravdu chete smazat společnost {0}?', {
                            0: deleteDialogState.company.name,
                        })}
                </DeleteDialog>
                <Formik<Values>
                    initialValues={{ [NAME_FIELD]: '' }}
                    onSubmit={addCompany}
                    validationSchema={validationSchema}
                >
                    <AddDialog
                        isVisible={createDialogState}
                        isLoading={loading}
                        onClose={closeDialog}
                        onCancel={closeDialog}
                    />
                </Formik>
                <Pagination
                    page={page}
                    pages={pagination.pageCount || 0}
                    nextPage={pagination.nextPage}
                    prevPage={pagination.prevPage}
                    onChange={changePage}
                />
            </BaseCard>
        </BasePageLayout>
    );

    async function addCompany(values: Values) {
        const result = await dispatch(
            AdminCustomerSlice.actions.createCustomer({ name: values.name }),
        ).unwrap();

        if (isSuccessfulResponse(result.status)) {
            navigate(
                generatePath(ADMIN_COMPANY_PATH, {
                    companyId: result.data.id.toString(),
                }),
            );
        }
    }

    function deleteCompany(company?: IAdminCustomer) {
        return () => {
            if (!company) {
                return;
            }
            // dispatch()
            throw 'Not implemented';
        };
    }

    function showDeleteDialog(company: IAdminCustomer) {
        setDeleteDialogState({ active: true, company });
    }

    function showCreateDialog() {
        setCreateDialogState(true);
    }

    function closeDialog() {
        setDeleteDialogState({ active: false, company: undefined });
        setCreateDialogState(false);
    }
}

type ItemProps = {
    company: IAdminCustomer;
    onDelete: (company: IAdminCustomer) => void;
    isLoading?: boolean;
};

function Item(props: ItemProps) {
    const { company, onDelete, isLoading } = props;

    return (
        <Table.Row className={styles.row}>
            <Link
                to={generatePath(ADMIN_COMPANY_PATH, {
                    companyId: company.id.toString(),
                })}
            >
                <div className={styles.name}>{company.name}</div>
            </Link>
            <ListDeleteButton onClick={deleteCompany(company)} />
        </Table.Row>
    );

    function deleteCompany(company: IAdminCustomer) {
        return () => {
            if (isLoading) {
                return;
            }

            onDelete(company);
        };
    }
}

type AddDialogProps = {
    isVisible: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onCancel: () => void;
};

function AddDialog(props: AddDialogProps) {
    const translate = useTranslate();

    return (
        <CreateDialog<Values>
            heading={translate('Vytvorit společnost')}
            {...props}
        >
            <div className={styles.form}>
                <FormInput
                    name={NAME_FIELD}
                    label={translate('Nazev společnosti')}
                />
            </div>
        </CreateDialog>
    );
}
